// src/logic/matcher.config.ts
// (V9 Patch 3: proud-normalization + strengths & metrics nudges)

export const matcherConfig = {
  // --- General Settings ---
  cooldownMsDefault: 1500,

  // --- Normalization Data ---
  stopwords: new Set<string>([
    "the","a","an","and","or","but","if","in","on","at","to","of","for","with","as",
    "is","are","am","be","being","been","was","were","do","does","did","doing",
    "have","has","had","having",
    "i","you","we","they","he","she","it",
    "this","that","these","those",
    "what","which","who","whom","whose","how","when","where","why",
    "will","would","can","could","may","might",
    "over","there","here","from","into","about","around","up","down","out","off","than","then","so","just","now","today","tomorrow","yesterday","during","big",
    // determiners/pronouns for elastic gaps
    "your","my","our","their","his","her","its"
  ]),
  microSynonyms: {
    // manage family
    "manage":"manage","managing":"manage","managed":"manage","manager":"manage","management":"manage",
    "lead":"manage","led":"manage","run":"manage","running":"manage",

    // project / initiative
    "project":"project","projects":"project","initiative":"project","initiatives":"project",

    // prioritize
    "prioritize":"prioritize","prioritizing":"prioritize","prioritized":"prioritize",

    // align family
    "aligned":"align","alignment":"align","aligns":"align","aligning":"align","align":"align","keeping":"keep",

    // strengths / weaknesses
    "strength":"strength","strengths":"strength","strongest":"strength","excel":"strength","excels":"strength","excelling":"strength",
    "weakness":"weakness","weaknesses":"weakness",

    // salary / comp
    "salary":"salary","compensation":"salary","pay":"salary","range":"range","ote":"ote",

    // metrics
    "kpi":"kpi","kpis":"kpi","metric":"metric","metrics":"metric",

    // scale / teams
    "scale":"scale","scaled":"scale","scaling":"scale",
    "teams":"team","team":"team",

    // area(s)
    "areas":"area","area":"area",

    // conflict / disagree / handle
    "disagreed":"disagree","disagreeing":"disagree","handled":"handle","handling":"handle",

    // stakeholders singularize
    "stakeholders":"stakeholder","stakeholder":"stakeholder",

    // job/role mapping
    "job":"role","jobs":"role","roles":"role","role":"role",

    // ----- NEW: proud/“specially” STT normalization -----
    "specially":"especially",
    "proudest":"proud"
  } as Record<string, string>,

  // --- Matching Strategy Data ---
  genericTails: new Set(["project","example","time","situation","story","result"]),
  
  highSignalAnchors: new Set<string>([
    "strength","project","manage","career","leadership","challenge","metrics","metric","kpi","salary","align",
    // additions
    "role","company","conflict","resume","roadblock"
  ]),

  // Bonus for Substring when a shared head anchor is present
  substringHeadAnchorBonus: 0.35,

  // --- Fragment Gate Data ---
  singleTokenWhitelist: new Set<string>(["cv","resume"]),
  denyFragments: new Set<string>(["a o","to do","tell","there is something"]),

  // --- Tail Helper Data ---
  gapNounAllow: new Set<string>(["project","projects","team","teams","workstream","workstreams","roadmap","roadmaps"]),

  // --- Fuzzy Data ---
  fuzzyOverlapExclusions: new Set<string>([
    "yourself","myself","ourselves","himself","herself","me","us","bit","please","share","tell","talk"
  ]),

  // --- Filler Data ---
  fillerShortSet: new Set<string>(['um','uh','er','erm','ah','eh','mm','mmm','hm','hmm','uhm','uhh','huh','oh','ooh','ahem','ok','okay','yeah','oops']),
  fillerPhrases: ['you know','i mean','let me think','let me see','mm hmm','uh huh','uh uh'],

  // --- Keyword Nudge Overrides (Data-driven) ---
  // These only re-rank within the gated pool — no increase in recall/blast radius.
  keywordNudges: [
    {
      noteId: "core10-elevator-pitch",
      patterns: [ /\belevator\s+pitch\b/, /\bpitch\b.*\byourself\b/ ]
    },
    {
      noteId: "core10-star-leadership",
      patterns: [ /\bled(?:\s+a)?\s+team\b/, /\btook\s+ownership\b/ ]
    },
    {
      noteId: "core10-project-management",
      patterns: [
        /\bplans?\b.*\breset(s|ting|ted)?\b/,
        /\bplans?\b.*\bre[-\s]?establish(ed|ing)?\b/,
        /\bplans?\b.*\balign(ment|ed|ing|s)?\b/,
        /\bproject\s+manage(ment|r)?\b/,
        /\baligned\b.*\btransitions?\b/
      ]
    },
    {
      // Expanded: stabilize “proud/achievement” language toward Strengths
      noteId: "core10-strengths",
      patterns: [
        /\bstrengths?\b/,
        /\bproud\b/,
        /\bproudest\b/,
        /\b(?:especially|specially)\s+proud\b/,
        /\b(big(gest)?\s+win|wins?)\b/,
        /\bachieve(?:ment|ments)\b/,
        /\baccomplish(?:ment|ments)?\b/
      ]
    },

    // ----- Optional, safe: help Metrics win tie-breaks on common phrasing -----
    {
      noteId: "core10-metrics",
      patterns: [
        /\bhitting\s+the\s+mark\b/,
        /\bmetrics?\b/,
        /\bkpis?\b/,
        /\bmeasure\s+success\b/,
        /\bwhat\s+does\s+success\s+look\s+like\b/
      ]
    },
    {
      // If your metrics card uses a different ID, keep both (harmless if not present)
      noteId: "core10-metrics-success",
      patterns: [
        /\bhitting\s+the\s+mark\b/,
        /\bmetrics?\b/,
        /\bkpis?\b/,
        /\bmeasure\s+success\b/,
        /\bwhat\s+does\s+success\s+look\s+like\b/
      ]
    }
  ]
};
